<template>
  <modal-card
    title="Interviews"
    fixed
    padding
    :full="!hasActions"
    :loading="loading"
    @close="$emit('close')"
  >
    <v-alert
      v-if="interviewError"
      transition="fade-transition"
      class="mb-6 pa-2"
      :type="interviewError.type"
      v-html="interviewError.message"
    />

    <v-form v-if="showForm" v-model="isFormValid" @submit.prevent>
      <v-text-field
        v-if="missingEmail"
        v-model="email"
        label="Email"
        :rules="emailRules"
        :disabled="saving"
      />

      <v-text-field
        v-if="missingZipCode"
        v-model="zipCode"
        label="Zip Code"
        class="mt-2"
        :rules="zipCodeRules"
        :disabled="saving"
      />
    </v-form>

    <template #actions v-if="hasActions">
      <v-btn
        v-if="showForm"
        color="appMain"
        :loading="saving"
        :disabled="!isFormValid"
        @click="saveFields"
      >
        Save
      </v-btn>

      <v-btn
        v-if="canSkipContact"
        color="appMain"
        text
        @click="skipContact"
        :loading="saving"
      >
        Next Contact
      </v-btn>
    </template>
  </modal-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import validationRules from '@/lib/validationRules';

const CONTACT_ERRORS = {
  recruit_id: {
    type: 'error',
    message: 'Unable to get recruit ID from contact.',
  },
  training_set: {
    type: 'info',
    message:
      'This contact is already set for training. <br> No interview required.',
  },
  email: {
    type: 'info',
    message: 'Set email for this contact',
  },
  zip_code: {
    type: 'info',
    message: 'Set zip code for this contact',
  },
  email_and_zip: {
    type: 'info',
    message: 'Set email and zip code for this contact',
  },
};

export default {
  name: 'ContactInterviewError',
  props: {
    contactId: String,
    contact: {
      type: Object,
      default: () => ({}),
    },
    serverError: {
      type: Object,
      default: null,
    },
    loading: Boolean,
  },
  data() {
    return {
      email: '',
      zipCode: '',
      saving: false,
      isFormValid: false,
    };
  },

  computed: {
    ...mapGetters('fields', ['getSingleField']),
    canSkipContact() {
      return !!this.serverError;
    },
    hasRecruitId() {
      return Boolean(this.contact?.recruit_id);
    },
    missingEmail() {
      return !this.contact?.email;
    },
    missingZipCode() {
      return !this.contact?.zip_code;
    },
    showForm() {
      return this.missingEmail || this.missingZipCode;
    },
    hasActions() {
      return this.showForm || this.canSkipContact;
    },
    interviewError() {
      if (!this.contact) return;

      if (this.missingEmail && this.missingZipCode)
        return CONTACT_ERRORS.email_and_zip;

      if (this.missingZipCode) return CONTACT_ERRORS.zip_code;

      if (this.missingEmail) return CONTACT_ERRORS.email;

      if (!this.hasRecruitId) return CONTACT_ERRORS.recruit_id;

      if (this.contact.training_set) return CONTACT_ERRORS.training_set;

      return this.serverError;
    },
    emailRules() {
      return [
        (v) => validationRules.checkRequired(v, 'Email'),
        (v) => validationRules.checkEmail(v) || 'Email must be valid',
      ];
    },
    zipCodeRules() {
      return [(v) => validationRules.checkRequired(v, 'Zip Code')];
    },
  },
  methods: {
    ...mapActions(['updateContact', 'updateContactField']),

    async saveFields() {
      if (!this.isFormValid) return;

      this.saving = true;

      try {
        const emailField = this.getSingleField('email');
        const zipField = this.getSingleField('zip_code');
        const params = { [emailField.bucketId]: {} };

        if (this.missingEmail && this.email) {
          params[emailField.bucketId][emailField.name] = this.email;
        }

        if (this.missingZipCode && this.zipCode) {
          params[emailField.bucketId][zipField.name] = this.zipCode;
        }

        await this.updateContact({
          contactId: this.contactId,
          params,
        });
      } finally {
        this.saving = false;
      }
    },

    async skipContact() {
      try {
        this.saving = true;

        await this.updateContactField({
          contactId: this.contactId,
          field: 'last_contacted',
          value: new Date(),
        });

        this.$emit('close');
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
